/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import ThemedLoadingSpinner from './ThemedLoadingSpinner';
import AccountDeletionModal from './AccountDeletionModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import '../styles/RecruiterSettings.css';
import EmployerFilterDropdown from './EmployerFilterDropdown';

const COMPANY_SIZE_OPTIONS = [
  { value: '', label: 'Select size' },
  { value: '1-10', label: '1-10 employees' },
  { value: '11-50', label: '11-50 employees' },
  { value: '51-200', label: '51-200 employees' },
  { value: '201-500', label: '201-500 employees' },
  { value: '501-1000', label: '501-1000 employees' },
  { value: '1000+', label: '1000+ employees' },
];

const INDUSTRY_OPTIONS = [
  { value: '', label: 'Select industry' },
  { value: 'Technology', label: 'Technology' },
  { value: 'Healthcare', label: 'Healthcare' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Education', label: 'Education' },
  { value: 'Manufacturing', label: 'Manufacturing' },
  { value: 'Retail', label: 'Retail' },
  { value: 'Other', label: 'Other' },
];

const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time' },
  { value: 'America/Chicago', label: 'Central Time' },
  { value: 'America/Denver', label: 'Mountain Time' },
  { value: 'America/Los_Angeles', label: 'Pacific Time' },
  { value: 'Europe/London', label: 'London' },
  { value: 'Europe/Paris', label: 'Paris' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
];

const DATE_FORMAT_OPTIONS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
];

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'JPY', label: 'JPY (¥)' },
  { value: 'CAD', label: 'CAD (C$)' },
];

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
];

const THEME_OPTIONS = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'auto', label: 'Auto' },
];

const RecruiterSettings = ({ user, onUpdateUser, isMobileView = false, useCustomDropdown = false }) => {
  const shouldUseCustomDropdown = isMobileView || useCustomDropdown;
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('security');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Profile Settings
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    linkedinProfile: '',
    bio: ''
  });

  // Company Settings
  const [companyData, setCompanyData] = useState({
    companyName: '',
    companyEmail: '',
    companyPhone: '',
    companyWebsite: '',
    companySize: '',
    industry: '',
    companyDescription: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: ''
  });

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    newApplications: true,
    applicationUpdates: true,
    interviewReminders: true,
    weeklyReports: true,
    marketingEmails: false,
    pushNotifications: true,
    smsNotifications: false
  });

  // Preferences
  const [preferences, setPreferences] = useState({
    timezone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    currency: 'USD',
    language: 'en',
    theme: 'light',
    autoArchiveJobs: false,
    autoRejectThreshold: 50,
    matchScoreThreshold: 70,
    maxApplicationsPerJob: 100,
    calendlyUrl: '',
    useCalendly: false
  });

  // Security Settings
  const [securityData, setSecurityData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorEnabled: false
  });

  useEffect(() => {
    if (user) {
      // Load profile data
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || user.recruiterPhone || '',
        jobTitle: user.jobTitle || '',
        linkedinProfile: user.linkedinProfile || '',
        bio: user.bio || ''
      });

      // Load company data
      setCompanyData({
        companyName: user.companyName || '',
        companyEmail: user.companyEmail || '',
        companyPhone: user.companyPhone || '',
        companyWebsite: user.companyWebsite || '',
        companySize: user.companySize || '',
        industry: user.industry || '',
        companyDescription: user.companyDescription || '',
        address: user.address || '',
        city: user.city || '',
        state: user.state || '',
        country: user.country || '',
        postalCode: user.postalCode || ''
      });

      // Load notification settings
      setNotificationSettings({
        emailNotifications: user.emailNotifications !== false,
        newApplications: user.newApplications !== false,
        applicationUpdates: user.applicationUpdates !== false,
        interviewReminders: user.interviewReminders !== false,
        weeklyReports: user.weeklyReports !== false,
        marketingEmails: user.marketingEmails === true,
        pushNotifications: user.pushNotifications !== false,
        smsNotifications: user.smsNotifications === true
      });

      // Load preferences
      setPreferences({
        timezone: user.timezone || 'UTC',
        dateFormat: user.dateFormat || 'MM/DD/YYYY',
        currency: user.currency || 'USD',
        language: user.language || 'en',
        theme: user.theme || 'light',
        autoArchiveJobs: user.autoArchiveJobs === true,
        autoRejectThreshold: user.autoRejectThreshold || 50,
        matchScoreThreshold: user.matchScoreThreshold || 70,
        maxApplicationsPerJob: user.maxApplicationsPerJob || 100,
        calendlyUrl: user.calendlyUrl || '',
        useCalendly: user.useCalendly === true
      });

      // Load security settings
      setSecurityData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        twoFactorEnabled: user.twoFactorEnabled === true
      });
    }
  }, [user]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const renderSettingsSelect = (value, onChange, options) => (
    shouldUseCustomDropdown ? (
      <EmployerFilterDropdown
        showCounts={false}
        value={value}
        onChange={onChange}
        options={options}
      />
    ) : (
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    )
  );

  const handleSave = async (section) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      let data = {};

      switch (section) {
        case 'profile':
          endpoint = '/api/recruiter/profile';
          data = profileData;
          break;
        case 'company':
          endpoint = '/api/recruiter/company';
          data = companyData;
          break;
        case 'notifications':
          endpoint = '/api/recruiter/notifications';
          data = notificationSettings;
          break;
        case 'preferences':
          endpoint = '/api/recruiter/preferences';
          data = preferences;
          break;
        case 'security':
          // Validate password change if new password is provided
          if (securityData.newPassword || securityData.confirmPassword) {
            // If changing password, all fields are required
            if (!securityData.currentPassword) {
              showMessage('error', 'Please enter your current password');
              setSaving(false);
              return;
            }
            if (!securityData.newPassword) {
              showMessage('error', 'Please enter a new password');
              setSaving(false);
              return;
            }
            if (!securityData.confirmPassword) {
              showMessage('error', 'Please confirm your new password');
              setSaving(false);
              return;
            }
            // Validate password match
          if (securityData.newPassword !== securityData.confirmPassword) {
            showMessage('error', 'New passwords do not match');
              setSaving(false);
            return;
            }
            // Validate password length
            if (securityData.newPassword.length < 8) {
              showMessage('error', 'Password must be at least 8 characters long');
              setSaving(false);
              return;
            }
            // Validate password complexity (at least one letter and one number)
            const hasLetter = /[a-zA-Z]/.test(securityData.newPassword);
            const hasNumber = /[0-9]/.test(securityData.newPassword);
            if (!hasLetter || !hasNumber) {
              showMessage('error', 'Password must contain at least one letter and one number');
              setSaving(false);
              return;
            }
          }
          // Use plural /recruiters prefix to match backend blueprint URL (/api/recruiters/security)
          endpoint = '/api/recruiters/security';
          // Only send password fields if they are being changed
          data = {
            twoFactorEnabled: securityData.twoFactorEnabled
          };
          if (securityData.newPassword) {
            data.currentPassword = securityData.currentPassword;
            data.newPassword = securityData.newPassword;
            data.confirmPassword = securityData.confirmPassword;
          }
          break;
        default:
          return;
      }

      const response = await fetch(buildApiUrl(endpoint), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        const result = await response.json();
        if (onUpdateUser && result.user) {
          onUpdateUser(result.user);
        }
        showMessage('success', result.message || `${section.charAt(0).toUpperCase() + section.slice(1)} updated successfully!`);
        
        // Clear password fields after successful security update
        if (section === 'security') {
          setSecurityData(prev => ({
            ...prev,
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          }));
        }
      } else {
        const error = await response.json();
        showMessage('error', error.error || error.message || 'Failed to update settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      showMessage('error', 'Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const renderProfileTab = () => (
    <div className="settings-section">
      <h3>Profile Information</h3>
      <div className="form-grid">
        <div className="form-group">
          <label>First Name</label>
          <input
            type="text"
            value={profileData.firstName}
            onChange={(e) => setProfileData({...profileData, firstName: e.target.value})}
            placeholder="Enter your first name"
          />
        </div>
        <div className="form-group">
          <label>Last Name</label>
          <input
            type="text"
            value={profileData.lastName}
            onChange={(e) => setProfileData({...profileData, lastName: e.target.value})}
            placeholder="Enter your last name"
          />
        </div>
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={profileData.email}
            onChange={(e) => setProfileData({...profileData, email: e.target.value})}
            placeholder="Enter your email"
          />
        </div>
        <div className="form-group">
          <label>Phone</label>
          <input
            type="tel"
            value={profileData.phone}
            onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
            placeholder="Enter your phone number"
          />
        </div>
        <div className="form-group">
          <label>Job Title</label>
          <input
            type="text"
            value={profileData.jobTitle}
            onChange={(e) => setProfileData({...profileData, jobTitle: e.target.value})}
            placeholder="e.g., Senior Recruiter"
          />
        </div>
        <div className="form-group">
          <label>LinkedIn Profile</label>
          <input
            type="url"
            value={profileData.linkedinProfile}
            onChange={(e) => setProfileData({...profileData, linkedinProfile: e.target.value})}
            placeholder="https://linkedin.com/in/yourprofile"
          />
        </div>
      </div>
      <div className="form-group">
        <label>Bio</label>
        <textarea
          value={profileData.bio}
          onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
          placeholder="Tell us about yourself..."
          rows="4"
        />
      </div>
      <button 
        className="btn btn-primary"
        onClick={() => handleSave('profile')}
        disabled={saving}
      >
        {saving ? 'Saving...' : 'Save Profile'}
      </button>
    </div>
  );

  const renderCompanyTab = () => (
    <div className="settings-section">
      <h3>Company Information</h3>
      <div className="form-grid">
        <div className="form-group">
          <label>Company Name</label>
          <input
            type="text"
            value={companyData.companyName}
            onChange={(e) => setCompanyData({...companyData, companyName: e.target.value})}
            placeholder="Enter company name"
          />
        </div>
        <div className="form-group">
          <label>Company Email</label>
          <input
            type="email"
            value={companyData.companyEmail}
            onChange={(e) => setCompanyData({...companyData, companyEmail: e.target.value})}
            placeholder="company@example.com"
          />
        </div>
        <div className="form-group">
          <label>Company Phone</label>
          <input
            type="tel"
            value={companyData.companyPhone}
            onChange={(e) => setCompanyData({...companyData, companyPhone: e.target.value})}
            placeholder="+1 (555) 123-4567"
          />
        </div>
        <div className="form-group">
          <label>Website</label>
          <input
            type="url"
            value={companyData.companyWebsite}
            onChange={(e) => setCompanyData({...companyData, companyWebsite: e.target.value})}
            placeholder="https://company.com"
          />
        </div>
        <div className="form-group">
          <label>Company Size</label>
          {renderSettingsSelect(
            companyData.companySize,
            (value) => setCompanyData({ ...companyData, companySize: value }),
            COMPANY_SIZE_OPTIONS
          )}
        </div>
        <div className="form-group">
          <label>Industry</label>
          {renderSettingsSelect(
            companyData.industry,
            (value) => setCompanyData({ ...companyData, industry: value }),
            INDUSTRY_OPTIONS
          )}
        </div>
      </div>
      <div className="form-group">
        <label>Company Description</label>
        <textarea
          value={companyData.companyDescription}
          onChange={(e) => setCompanyData({...companyData, companyDescription: e.target.value})}
          placeholder="Describe your company..."
          rows="4"
        />
      </div>
      <h4>Address</h4>
      <div className="form-grid">
        <div className="form-group full-width">
          <label>Street Address</label>
          <input
            type="text"
            value={companyData.address}
            onChange={(e) => setCompanyData({...companyData, address: e.target.value})}
            placeholder="123 Main Street"
          />
        </div>
        <div className="form-group">
          <label>City</label>
          <input
            type="text"
            value={companyData.city}
            onChange={(e) => setCompanyData({...companyData, city: e.target.value})}
            placeholder="City"
          />
        </div>
        <div className="form-group">
          <label>State/Province</label>
          <input
            type="text"
            value={companyData.state}
            onChange={(e) => setCompanyData({...companyData, state: e.target.value})}
            placeholder="State"
          />
        </div>
        <div className="form-group">
          <label>Country</label>
          <input
            type="text"
            value={companyData.country}
            onChange={(e) => setCompanyData({...companyData, country: e.target.value})}
            placeholder="Country"
          />
        </div>
        <div className="form-group">
          <label>Postal Code</label>
          <input
            type="text"
            value={companyData.postalCode}
            onChange={(e) => setCompanyData({...companyData, postalCode: e.target.value})}
            placeholder="12345"
          />
        </div>
      </div>
      <button 
        className="btn btn-primary"
        onClick={() => handleSave('company')}
        disabled={saving}
      >
        {saving ? 'Saving...' : 'Save Company Info'}
      </button>
    </div>
  );

  const renderNotificationsTab = () => (
    <div className="settings-section">
      <h3>Notification Preferences</h3>
      <div className="notification-settings">
        <div className="notification-group">
          <h4>Email Notifications</h4>
          <div className="toggle-group">
            <label className="toggle-item">
              <input
                type="checkbox"
                checked={notificationSettings.emailNotifications}
                onChange={(e) => setNotificationSettings({...notificationSettings, emailNotifications: e.target.checked})}
              />
              <span className="toggle-label">Enable email notifications</span>
            </label>
            <label className="toggle-item">
              <input
                type="checkbox"
                checked={notificationSettings.newApplications}
                onChange={(e) => setNotificationSettings({...notificationSettings, newApplications: e.target.checked})}
              />
              <span className="toggle-label">New applications</span>
            </label>
            <label className="toggle-item">
              <input
                type="checkbox"
                checked={notificationSettings.applicationUpdates}
                onChange={(e) => setNotificationSettings({...notificationSettings, applicationUpdates: e.target.checked})}
              />
              <span className="toggle-label">Application status updates</span>
            </label>
            <label className="toggle-item">
              <input
                type="checkbox"
                checked={notificationSettings.interviewReminders}
                onChange={(e) => setNotificationSettings({...notificationSettings, interviewReminders: e.target.checked})}
              />
              <span className="toggle-label">Interview reminders</span>
            </label>
            <label className="toggle-item">
              <input
                type="checkbox"
                checked={notificationSettings.weeklyReports}
                onChange={(e) => setNotificationSettings({...notificationSettings, weeklyReports: e.target.checked})}
              />
              <span className="toggle-label">Weekly reports</span>
            </label>
            <label className="toggle-item">
              <input
                type="checkbox"
                checked={notificationSettings.marketingEmails}
                onChange={(e) => setNotificationSettings({...notificationSettings, marketingEmails: e.target.checked})}
              />
              <span className="toggle-label">Marketing emails</span>
            </label>
          </div>
        </div>
        <div className="notification-group">
          <h4>Other Notifications</h4>
          <div className="toggle-group">
            <label className="toggle-item">
              <input
                type="checkbox"
                checked={notificationSettings.pushNotifications}
                onChange={(e) => setNotificationSettings({...notificationSettings, pushNotifications: e.target.checked})}
              />
              <span className="toggle-label">Push notifications</span>
            </label>
            <label className="toggle-item">
              <input
                type="checkbox"
                checked={notificationSettings.smsNotifications}
                onChange={(e) => setNotificationSettings({...notificationSettings, smsNotifications: e.target.checked})}
              />
              <span className="toggle-label">SMS notifications</span>
            </label>
          </div>
        </div>
      </div>
      <button 
        className="btn btn-primary"
        onClick={() => handleSave('notifications')}
        disabled={saving}
      >
        {saving ? 'Saving...' : 'Save Notifications'}
      </button>
    </div>
  );

  const renderPreferencesTab = () => (
    <div className="settings-section">
      <h3>Preferences</h3>
      <div className="form-grid">
        <div className="form-group">
          <label>Timezone</label>
          {renderSettingsSelect(
            preferences.timezone,
            (value) => setPreferences({ ...preferences, timezone: value }),
            TIMEZONE_OPTIONS
          )}
        </div>
        <div className="form-group">
          <label>Date Format</label>
          {renderSettingsSelect(
            preferences.dateFormat,
            (value) => setPreferences({ ...preferences, dateFormat: value }),
            DATE_FORMAT_OPTIONS
          )}
        </div>
        <div className="form-group">
          <label>Currency</label>
          {renderSettingsSelect(
            preferences.currency,
            (value) => setPreferences({ ...preferences, currency: value }),
            CURRENCY_OPTIONS
          )}
        </div>
        <div className="form-group">
          <label>Language</label>
          {renderSettingsSelect(
            preferences.language,
            (value) => setPreferences({ ...preferences, language: value }),
            LANGUAGE_OPTIONS
          )}
        </div>
        <div className="form-group">
          <label>Theme</label>
          {renderSettingsSelect(
            preferences.theme,
            (value) => setPreferences({ ...preferences, theme: value }),
            THEME_OPTIONS
          )}
        </div>
        <div className="form-group">
          <label>Auto-reject Threshold</label>
          <input
            type="number"
            min="0"
            max="100"
            value={preferences.autoRejectThreshold}
            onChange={(e) => setPreferences({...preferences, autoRejectThreshold: parseInt(e.target.value)})}
          />
          <small>Applications below this match score will be automatically rejected</small>
        </div>
        <div className="form-group">
          <label>Match Score Threshold</label>
          <input
            type="number"
            min="0"
            max="100"
            value={preferences.matchScoreThreshold}
            onChange={(e) => setPreferences({...preferences, matchScoreThreshold: parseInt(e.target.value)})}
          />
          <small>Minimum match score to show applications</small>
        </div>
        <div className="form-group">
          <label>Max Applications per Job</label>
          <input
            type="number"
            min="1"
            max="1000"
            value={preferences.maxApplicationsPerJob}
            onChange={(e) => setPreferences({...preferences, maxApplicationsPerJob: parseInt(e.target.value)})}
          />
          <small>Maximum number of applications to accept per job posting</small>
        </div>
      </div>
      <div className="toggle-group">
        <label className="toggle-item">
          <input
            type="checkbox"
            checked={preferences.autoArchiveJobs}
            onChange={(e) => setPreferences({...preferences, autoArchiveJobs: e.target.checked})}
          />
          <span className="toggle-label">Auto-archive jobs after 30 days of inactivity</span>
        </label>
      </div>
      
      <div className="settings-divider"></div>
      
      <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Calendly Integration</h3>
      <div className="form-group">
        <label>
          <input
            type="checkbox"
            checked={preferences.useCalendly}
            onChange={(e) => setPreferences({...preferences, useCalendly: e.target.checked})}
            style={{ marginRight: '8px' }}
          />
          Enable Calendly for Interview Scheduling
        </label>
        <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
          When enabled, candidates will book interviews through your Calendly calendar
        </small>
      </div>
      
      {preferences.useCalendly && (
        <div className="form-group" style={{ marginTop: '1rem' }}>
          <label>Calendly URL *</label>
          <input
            type="text"
            value={preferences.calendlyUrl}
            onChange={(e) => setPreferences({...preferences, calendlyUrl: e.target.value})}
            placeholder="https://calendly.com/your-username/meeting-type"
            required={preferences.useCalendly}
          />
          <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
            Enter your Calendly scheduling page URL. You can find this in your Calendly account settings.
            <br />
            Example: https://calendly.com/john-doe/30min
          </small>
        </div>
      )}
      <button 
        className="btn btn-primary"
        onClick={() => handleSave('preferences')}
        disabled={saving}
      >
        {saving ? 'Saving...' : 'Save Preferences'}
      </button>
    </div>
  );

  const handleDeleteAccount = async (deletionData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl('/api/user/settings/delete-account'), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(deletionData)
      });

      if (response.ok) {
        // Clear all local storage
        localStorage.clear();
        // Logout user
        if (logout) {
          logout();
        }
        // Navigate to home
        navigate('/');
        alert('Your account has been permanently deleted.');
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete account');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  };

  const renderSecurityTab = () => (
    <div className="settings-section">
      <h3>Change Password</h3>
      <div className="form-grid">
        <div className="form-group">
          <label>Current Password</label>
          <input
            type="password"
            value={securityData.currentPassword}
            onChange={(e) => setSecurityData({...securityData, currentPassword: e.target.value})}
            placeholder="Enter current password"
          />
        </div>
        <div className="form-group">
          <label>New Password</label>
          <input
            type="password"
            value={securityData.newPassword}
            onChange={(e) => setSecurityData({...securityData, newPassword: e.target.value})}
            placeholder="Enter new password"
          />
          <small>
            Password must be at least 8 characters and contain at least one letter and one number
          </small>
        </div>
        <div className="form-group">
          <label>Confirm New Password</label>
          <input
            type="password"
            value={securityData.confirmPassword}
            onChange={(e) => setSecurityData({...securityData, confirmPassword: e.target.value})}
            placeholder="Confirm new password"
          />
        </div>
      </div>
      <button
        type="button"
        className="btn btn-primary settings-action-btn"
        onClick={() => handleSave('security')}
        disabled={saving}
      >
        {saving ? 'Saving...' : 'Update Password'}
      </button>

      <div className="settings-divider" />

      <h3>Two-Factor Authentication</h3>
      <div className="toggle-group settings-2fa-panel">
        <label className="toggle-item">
          <input
            type="checkbox"
            checked={securityData.twoFactorEnabled}
            onChange={(e) => setSecurityData({...securityData, twoFactorEnabled: e.target.checked})}
          />
          <span className="toggle-label">Enable two-factor authentication</span>
        </label>
        <small>
          Add an extra layer of security to your account by requiring a verification code in addition to your password
        </small>
      </div>
      <button
        type="button"
        className="btn btn-primary settings-action-btn"
        onClick={() => handleSave('security')}
        disabled={saving}
      >
        {saving ? 'Saving...' : 'Update Security Settings'}
      </button>

      <div className="account-deletion-section">
        <h3>
          <FontAwesomeIcon icon={faTrash} /> Danger Zone
        </h3>
        <p>
          Once you delete your account, there is no going back. Please be certain.
        </p>
        <button
          type="button"
          className="btn-danger"
          onClick={() => setShowDeleteModal(true)}
        >
          <FontAwesomeIcon icon={faTrash} /> Delete Account
        </button>
      </div>
    </div>
  );

  if (loading) {
    return <ThemedLoadingSpinner theme="recruiter" size="large" text="Loading settings..." />;
  }

  return (
    <div className={`recruiter-settings${isMobileView ? ' employer-settings-mobile' : ''}`} style={isMobileView ? undefined : { padding: '20px', margin: '0' }}>
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="settings-container">
        <div className="settings-content">
          {activeTab === 'security' && renderSecurityTab()}
        </div>
      </div>

      <AccountDeletionModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDeleteAccount}
        accountType="employer"
      />
    </div>
  );
};

export default RecruiterSettings;
